import {createFocusTrap} from 'focus-trap';
import {gsap} from 'gsap';
import Ready from '../utils/ready.js';

const GLOBAL_SEARCH_SETTINGS = {
    minimumQueryLength: 2,
    idleDelay: 200,
};

const FACET_KEYWORDS = {
    clinician: [
        'consultant',
        'doctor',
        'dr',
        'specialist',
        'surgeon',
        'clinician',
    ],
};

// Removes the first facet keyword (singular or plural) from the query.
function extractFacetFromQuery(query) {
    const trimmed = query.trim();
    const tokens = trimmed.split(/\s+/);
    const match = Object.entries(FACET_KEYWORDS)
        .map(([group, keywords]) => {
            const index = tokens.findIndex((token) =>
                keywords.some((keyword) =>
                    new RegExp(`^${keyword}s?\\.?$`, 'i').test(token),
                ),
            );
            if (index === -1) return null;
            const remaining = tokens.filter((_, i) => i !== index).join(' ');
            return remaining.length >= GLOBAL_SEARCH_SETTINGS.minimumQueryLength
                ? {query: remaining, contentGroup: group}
                : null;
        })
        .find(Boolean);
    return match || {query: trimmed, contentGroup: null};
}

Ready(() => {
    let timerId;
    let initialised = false;
    let searchVisible = false;
    async function loadScripts() {
        const {liteClient} = await import('algoliasearch/lite');
        const instantsearchModule = await import('instantsearch.js');
        const instantsearch = instantsearchModule.default;
        const DOMPurify = (await import('dompurify')).default;
        const {searchBox, hits, pagination, configure, refinementList, panel} =
            await import('instantsearch.js/es/widgets');
        init({
            liteClient,
            instantsearch,
            searchBox,
            hits,
            pagination,
            configure,
            refinementList,
            panel,
            DOMPurify,
        });
    }

    const searchField = document.querySelector('#search-box');
    const searchWrapper = document.querySelector('.search-box-wrapper');
    const searchTrigger = document.querySelector('#search-trigger');
    const searchClose = document.querySelector('.close-search');

    function toggleSearchVisibility() {
        searchVisible = !searchVisible;
        if (searchVisible) {
            searchTrigger.dataset.toggled = '1';
            document.querySelector('.ais-SearchBox-input')?.focus();
            focusTrap?.activate();
            gsap.set(searchWrapper, {
                position: 'absolute',
                clip: 'initial',
                whiteSpace: 'initial',
                overflow: 'hidden',
                height: 0,
            });
            gsap.to(searchWrapper, {
                height: 'auto',
                zIndex: 100,
                duration: 0.6,
                ease: 'power3.out',
                onComplete: () => {
                    searchWrapper.dataset.visible = '1';
                },
            });
        } else {
            gsap.to(searchWrapper, {
                height: 0,
                duration: 0.3,
                ease: 'power3.out',
                onComplete: () => {
                    searchTrigger.dataset.toggled = '0';
                    searchWrapper.dataset.visible = '0';
                },
            });
        }
    }

    const focusTrap = createFocusTrap('.search-box-wrapper', {
        clickOutsideDeactivates: true,
        onDeactivate: () => {
            toggleSearchVisibility();
        },
    });

    if (!document.querySelector('#search-trigger')) return;
    document.querySelector('#search-trigger').addEventListener(
        'mouseover',
        (e) => {
            e.preventDefault();
            if (!initialised) {
                loadScripts();
            }
        },
        {once: true},
    );
    document.querySelector('#search-trigger').addEventListener('click', (e) => {
        e.preventDefault();
        if (!initialised) {
            loadScripts();
            toggleSearchVisibility();
        } else if (!searchVisible) {
            // toggling is handled with the focusTrap onDeactivate when visible
            toggleSearchVisibility();
        }
    });

    const searchPage = document.querySelector('.search-page');

    if (searchPage) {
        if (!initialised) {
            loadScripts();
        }
    }
    searchClose.addEventListener('click', (e) => {
        e.preventDefault();
        toggleSearchVisibility();
    });

    if (!searchPage) {
        window.addEventListener('keypress', (e) => {
            if (searchVisible && e.key === 'Escape') {
                document.querySelector('#hits').dataset.show = '0';
            }
        });
    }

    function init(fns) {
        const {
            liteClient,
            instantsearch,
            searchBox,
            hits,
            pagination,
            configure,
            refinementList,
            DOMPurify,
            panel,
        } = fns;
        const searchClient = liteClient(
            '0JIZOCPQB1',
            'f446c9efada33fa663a7039c057f610c',
        );

        const searchIndex = 'primary_index';
        const search = instantsearch({
            indexName: searchIndex,
            routing: false,
            searchClient,
            insights: true,
            onStateChange({uiState, setUiState}) {
                setUiState(uiState);
            },
            searchFunction(helper) {
                if (
                    helper.state.query.trim().length >=
                    GLOBAL_SEARCH_SETTINGS.minimumQueryLength
                ) {
                    helper.search();
                }
            },
        });
        initialised = true;

        // Facet applied by a keyword, so manual selections are never removed
        let keywordFacet = null;
        function syncKeywordFacet(group) {
            const {helper} = search;
            if (!helper) return;
            if (keywordFacet && keywordFacet !== group) {
                helper.removeDisjunctiveFacetRefinement(
                    'content_group',
                    keywordFacet,
                );
                keywordFacet = null;
            }
            if (
                group &&
                !helper.state.isDisjunctiveFacetRefined('content_group', group)
            ) {
                helper.addDisjunctiveFacetRefinement('content_group', group);
                keywordFacet = group;
            }
        }

        const contentTypeLabels = {
            unit: 'Specialist unit',
            treatment: 'Treatment',
            faq: 'FAQ',
            news: 'News',
            page: 'Info',
            event: 'Events',
            clinician: 'Specialists',
        };

        const contentGroupLabels = {
            clinician: 'Specialists',
            treatments: 'Treatments',
            news_events: 'News & events',
            info_faqs: 'Info & FAQs',
        };

        const searchResults = (hit, {html}) => {
            const icon = hit.icon || 'information';
            const image = hit.image
                ? html`<img src="${hit.image}" alt="" />`
                : html`<span>
                      <svg>
                          <use xlink:href="/public/svg/sprite.svg#${icon}" />
                      </svg>
                  </span>`;
            const clinicianSubtitle =
                hit.content_type === 'clinician' &&
                hit.expanded_content?.job_title
                    ? html`<p class="ais-search-result-body__subtitle">
                          ${hit.expanded_content.job_title}
                      </p>`
                    : '';

            const excerpt =
                hit.content_type === 'faq'
                    ? html`<p
                          dangerouslySetInnerHTML=${{__html: hit.excerpt}}
                      />`
                    : html`<p>${hit.excerpt || ''}</p>`;

            return html`<a
                href="${hit.url}"
                class="ais-search-result-link-wrapper ais-search-result-link-wrapper--${hit.content_type}"
                data-content-type="${hit.content_type}"
                data-insights-object-id="${hit.objectID}"
                data-insights-position="${hit.__position}"
                data-insights-query-id="${hit.__queryID}"
            >
                <div class="ais-search-result-body">
                    <span class="ais-search-result-type">
                        ${contentTypeLabels[hit.content_type] || 'Result'}
                    </span>
                    <h3>${hit.title}</h3>
                    ${clinicianSubtitle} ${excerpt}
                </div>
                <div class="ais-search-result-image">${image}</div>
            </a>`;
        };

        function toggleHitsVisibility(container, isVisible) {
            if (isVisible) {
                document.querySelector(container).classList.remove('hidden');
            } else {
                document.querySelector(container).classList.add('hidden');
            }
        }

        const refinementListWithPanel = panel({
            templates: {
                header: 'Filter by',
            },
        })(refinementList);

        search.addWidgets([
            configure({
                hitsPerPage: 8,
                attributesToRetrieve: [
                    'objectID',
                    'url',
                    'title',
                    'excerpt',
                    'image',
                    'icon',
                    'content_type',
                    'expanded_content.job_title',
                ],
                clickAnalytics: true,
            }),
            searchBox({
                container: searchField,
                placeholder: 'Search',
                autofocus: searchPage,
                showReset: true,
                showSubmit: true,
                showLoadingIndicator: true,
                cssClasses: {
                    root: 'ais-SearchBox',
                    form: 'ais-SearchBox-form',
                    input: 'ais-SearchBox-input fui-input',
                    submit: 'ais-SearchBox-submit',
                    reset: 'ais-SearchBox-reset',
                    loadingIcon: 'ais-SearchBox-loading',
                },
                reset() {
                    syncKeywordFacet(null);
                    document.querySelector(
                        '.search-results-container',
                    ).dataset.show = '0';
                    document
                        .querySelector('.search-results-block-no-results')
                        .classList.add('hidden');
                },
                queryHook(query, refine) {
                    clearTimeout(timerId);
                    const normalizedQuery = query.trim();
                    const canSearch =
                        normalizedQuery.length >=
                        GLOBAL_SEARCH_SETTINGS.minimumQueryLength;
                    document.querySelector(
                        '.search-results-container',
                    ).dataset.show = canSearch ? '1' : '0';
                    if (!canSearch) {
                        document
                            .querySelector('.search-results-block-primary')
                            .classList.add('hidden');
                        document
                            .querySelector('.search-results-block-no-results')
                            .classList.add('hidden');
                        syncKeywordFacet(null);
                        refine('');
                        return;
                    }
                    timerId = setTimeout(() => {
                        const {query: strippedQuery, contentGroup} =
                            extractFacetFromQuery(normalizedQuery);
                        syncKeywordFacet(contentGroup);
                        refine(strippedQuery);
                    }, GLOBAL_SEARCH_SETTINGS.idleDelay);
                },
            }),
            refinementListWithPanel({
                container: '#search-content-types',
                attribute: 'content_group',
                limit: 7,
                sortBy: ['name:asc'],
                transformItems(items) {
                    return items.map((item) => {
                        const label =
                            contentGroupLabels[item.value] || item.label;
                        return {
                            ...item,
                            label,
                            highlighted: label,
                        };
                    });
                },
            }),
            hits({
                container: '#hits-primary',
                // The html template tag still escapes text; FAQ excerpts are sanitised below
                escapeHTML: false,
                templates: {
                    empty: '',
                    item: searchResults,
                },
                transformItems(items) {
                    toggleHitsVisibility(
                        '.search-results-block-primary',
                        items.length > 0,
                    );
                    document
                        .querySelector('.search-results-block-no-results')
                        .classList.toggle('hidden', items.length > 0);
                    return items.map((item) =>
                        item.content_type === 'faq'
                            ? {
                                  ...item,
                                  excerpt: DOMPurify.sanitize(
                                      item.excerpt || '',
                                      {
                                          ALLOWED_TAGS: [
                                              'p',
                                              'br',
                                              'strong',
                                              'b',
                                              'em',
                                              'i',
                                              'ul',
                                              'ol',
                                              'li',
                                              'a',
                                          ],
                                          ALLOWED_ATTR: ['href'],
                                      },
                                  ),
                              }
                            : item,
                    );
                },
            }),
            pagination({
                container: '#pagination-primary',
                showFirst: false,
                showLast: false,
                scrollTo: true,
            }),
        ]);

        search.start();
    }
});
